import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { Cart, Discount, LineItem } from 'shared/types/cart';
import { ItemDataLayer, LineItemMapper } from './mapper/LineItemMapper';
import { gtmTrackEvent, gtmTrackEventError } from './gtmTrackEvent';
import { IEccomerceDataLayer } from './interface/IEccomerceDataLayer';
import { Account } from 'shared/types/account';
import { AccountMapper } from './mapper/AccountMapper';
import { GTMPromotionEvent } from '../../../../types/gtm-events/Ecommerce';
import { Money } from 'shared/types/product';

export const gtmRemoveFromCart = (
  lineItem: LineItem,
  quantity: number,
  cart: Cart,
  index: number,
  discounts?: Discount[],
  account?: Account,
) => {
  const eventKey = 'remove_from_cart';
  const item = LineItemMapper.fromLineItemToItemDataLayer(lineItem, quantity, index, discounts);
  const value = item.price! * quantity;

  const eventObject: IEccomerceDataLayer = {
    ecommerce: {
      currency: 'USD',
      value: value,
      items: [item],
    },
  };

  if (cart.lineItems?.length) {
    const cjEventObject = {
      products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
    };

    Object.assign(eventObject, { cjEvent: cjEventObject });
  }

  if (account) {
    const userObject = AccountMapper.fromAccountToGTMUser(account);

    Object.assign(eventObject, { user: userObject });
  }

  try {
    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmViewCart = (cart: Cart, account?: Account) => {
  const eventKey = 'view_cart';
  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: CurrencyHelpers.formatAmount(cart.totalPrice!),
        items: cart.lineItems
          ? cart.lineItems?.map((lineItem, index) =>
              LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count || 1, index + 1, cart.discountCodes),
            )
          : [],
      },
    };

    if (cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmRemoveFromWishlist = (
  lineItem: LineItem,
  index: number,
  wishlistId: string,
  wishlistName?: string,
  cart?: Cart,
  account?: Account,
) => {
  const eventKey = 'remove_from_wishlist';
  const parsedLineItem = LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count!, index);

  const priceWithoutDiscount = parsedLineItem.price! * (lineItem.count || 1);

  lineItem.price = lineItem.variant?.price;

  try {
    parsedLineItem.item_list_id = wishlistId;
    parsedLineItem.item_list_name = wishlistName;
    parsedLineItem.discount = lineItem.variant?.originalPrice
      ? parseFloat(
          (
            CurrencyHelpers.formatAmount(lineItem.variant?.originalPrice) * lineItem.count! -
            priceWithoutDiscount
          ).toFixed(2),
        )
      : 0;

    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: priceWithoutDiscount,
        items: [parsedLineItem],
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmQuantitySelector = ({
  clickAction,
  itemDataLayer,
  newQuantity,
  source,
  cart,
  account,
}: {
  itemDataLayer: ItemDataLayer;
  clickAction: 'increased' | 'reduced';
  newQuantity: number;
  index?: number;
  cart?: Cart;
  account?: Account;
  source: 'pdp' | 'cart' | 'wishlist' | 'Cross Sell overlay';
}) => {
  const eventKey = 'quantity_selector';

  const updateItemDataLayer: ItemDataLayer = {
    item_id: itemDataLayer.item_id,
    item_name: itemDataLayer.item_name,
    item_variant: itemDataLayer.item_variant,
    item_image: itemDataLayer.item_image,
    item_url: itemDataLayer.item_url,
    price: itemDataLayer.price,
    quantity: newQuantity,
  };

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        click_action: clickAction,
        currency: 'USD',
        value: itemDataLayer.price! * newQuantity,
        items: [updateItemDataLayer],
        source,
        cart_id: cart ? cart.cartId : '',
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmViewItem = ({
  itemDataLayer,
  cart,
  account,
}: {
  itemDataLayer: ItemDataLayer;
  cart?: Cart;
  account?: Account;
}) => {
  const eventKey = 'view_item';

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: itemDataLayer.price,
        items: [itemDataLayer],
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }
    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmAddToCart = ({
  itemDataLayer,
  quantity,
  cart,
  account,
}: {
  itemDataLayer: ItemDataLayer;
  quantity: number;
  cart?: Cart;
  account?: Account;
}) => {
  const eventKey = 'add_to_cart';

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: parseFloat((quantity * itemDataLayer.price!).toFixed(2)),
        items: [itemDataLayer],
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }
    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmAddToWishlist = ({
  itemDataLayer,
  quantity,
  cart,
  account,
}: {
  itemDataLayer: ItemDataLayer;
  quantity: number;
  cart?: Cart;
  account?: Account;
}) => {
  const eventKey = 'add_to_wishlist';

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: parseFloat((itemDataLayer.quantity! * itemDataLayer.price!).toFixed(2)),
        items: [itemDataLayer],
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmViewItemList = ({
  itemsDataLayer,
  filter_applied,
  filter_type,
  item_list_id,
  item_list_name,
  selected_option,
  cart,
  account,
}: {
  itemsDataLayer: ItemDataLayer[];
  item_list_id?: string;
  item_list_name?: string;
  filter_applied?: string;
  selected_option?: string;
  filter_type?: string;
  cart?: Cart;
  account?: Account;
}) => {
  const eventKey = 'view_item_list';

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        filter_applied,
        filter_type,
        item_list_id,
        item_list_name,
        selected_option,
        items: itemsDataLayer,
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmSelectItem = ({
  itemsDataLayer,
  filter_applied,
  filter_type,
  item_list_id,
  item_list_name,
  selected_option,
  cart,
  account,
}: {
  itemsDataLayer: ItemDataLayer;
  item_list_id?: string;
  item_list_name?: string;
  filter_applied?: string;
  selected_option?: string;
  filter_type?: string;
  cart?: Cart;
  account?: Account;
}) => {
  const eventKey = 'select_item';
  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        filter_applied,
        filter_type,
        item_list_id,
        item_list_name,
        selected_option,
        items: [itemsDataLayer],
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmBeginCheckout = ({ cart, account }: { cart: Cart; account?: Account }) => {
  const eventKey = 'begin_checkout';
  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: CurrencyHelpers.formatAmount(cart.totalPrice!),
        items: (cart.lineItems ?? []).map((lineItem, idx) =>
          LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count ?? 1, idx + 1, cart.discountCodes),
        ),
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmAddShippingInfo = ({
  cart,
  shippingTier,
  account,
}: {
  shippingTier: string;
  cart: Cart;
  account?: Account;
}) => {
  const eventKey = 'add_shipping_info';

  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: CurrencyHelpers.formatAmount(cart.totalPrice!),
        shipping_tier: shippingTier,
        coupon: cart.discountCodes?.[0]?.code ?? null,
        items: (cart.lineItems ?? []).map((lineItem, idx) =>
          LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count ?? 1, idx + 1, cart.discountCodes),
        ),
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmAddPaymentInfo = ({
  cart,
  paymentType,
  account,
}: {
  paymentType: string;
  cart: Cart;
  account?: Account;
}) => {
  const eventKey = 'add_payment_info';
  try {
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        currency: 'USD',
        value: CurrencyHelpers.formatAmount(cart.totalPrice!),
        payment_type: paymentType,
        coupon: cart.discountCodes?.[0]?.code ?? null,
        items: (cart.lineItems ?? []).map((lineItem, idx) =>
          LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count ?? 1, idx + 1, cart.discountCodes),
        ),
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmPurchase = ({
  cart,
  transactionId,
  account,
}: {
  cart: Cart;
  account?: Account;
  transactionId: string;
}) => {
  const eventKey = 'purchase';
  try {
    const cartTotalPriceWithoutTaxes: Money = cart.discountedAmount
      ? {
          centAmount: (cart.priceWIthoutDiscounts?.centAmount || 0) - (cart.discountedAmount?.centAmount || 0),
          currencyCode: 'USD',
          fractionDigits: 2,
        }
      : cart.priceWIthoutDiscounts || { centAmount: 0, fractionDigits: 2, currencyCode: 'USD' };
    const eventObject: IEccomerceDataLayer = {
      ecommerce: {
        transaction_id: transactionId,
        currency: 'USD',
        value: CurrencyHelpers.formatAmount(cartTotalPriceWithoutTaxes),
        tax: cart.totalTaxedPrice ? CurrencyHelpers.formatAmount(cart.totalTaxedPrice) : 0,
        shipping:
          cart.shippingInfo && cart.shippingInfo.price ? CurrencyHelpers.formatAmount(cart.shippingInfo.price) : 0,
        coupon: cart.discountCodes?.[0]?.code ?? null,
        items: (cart.lineItems ?? []).map((lineItem, idx) => ({
          ...LineItemMapper.fromLineItemToItemDataLayer(lineItem, lineItem.count ?? 1, idx + 1, cart.discountCodes),
          promotion_id: undefined,
          promotion_name: lineItem.variant?.promotionalMessageTitle,
        })),
        checkout_type: !!account ? 'registered' : 'guest',
        discounted_value: cart.discountedAmount ? CurrencyHelpers.formatAmount(cart.discountedAmount) : 0,
      },
    };

    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };

      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);

      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmHeroInteraction = (action: 'scroll' | 'click', name: string, position: number) => {
  const eventKey = 'hero_interaction';

  try {
    gtmTrackEvent(eventKey, { action, slide_name: name, slide_position: position });
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmShare = (evt: { method: string; content_type: string; item_id: string }) => {
  const eventKey = 'share';

  try {
    gtmTrackEvent(eventKey, evt);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmViewPromotion = (event: GTMPromotionEvent, cart?: Cart, account?: Account) => {
  const eventKey = 'view_promotion';
  const eventObject = { ecommerce: event };

  try {
    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };
      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);
      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};

export const gtmSelectPromotion = (event: GTMPromotionEvent, cart?: Cart, account?: Account) => {
  const eventKey = 'select_promotion';
  const eventObject = { ecommerce: event };

  try {
    if (cart && cart.lineItems?.length) {
      const cjEventObject = {
        products: cart.lineItems.map((lineItem) => LineItemMapper.fromLineItemToCJProductLayer(lineItem)),
      };
      Object.assign(eventObject, { cjEvent: cjEventObject });
    }

    if (account) {
      const userObject = AccountMapper.fromAccountToGTMUser(account);
      Object.assign(eventObject, { user: userObject });
    }

    gtmTrackEvent(eventKey, eventObject);
  } catch (error: any) {
    gtmTrackEventError(error, eventKey);
  }
};
